'use client';

import { useMemo, useState } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { ADMIN_EMPLOYEE_COLUMNS } from '@/app/(app)/admin/employees/columns';
import { CreateEmployeeDialog } from '@/app/(app)/admin/employees/dialog';
import { PageMain } from '@/app/(app)/page-main';
import { Popover } from '@/components/atoms/popover';
import { SearchSelectContent } from '@/components/atoms/select';
import { DataTable } from '@/components/data-table';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

export function AdminEmployeesPage() {
  const { office } = useOfficeCtx();
  const [officeId, setOfficeId] = useState(office.id);
  console.log('officeId: ', officeId);
  return (
    <PageMain title='Employees'>
      <div className='flex w-full items-center justify-between'>
        <OfficeSelector officeId={officeId} setOfficeId={setOfficeId} />
        <CreateEmployeeDialog />
      </div>
      <EmployeeTable key={officeId} officeId={officeId} />
    </PageMain>
  );
}

interface OfficeSelectorProps {
  officeId: string;
  setOfficeId: (officeId: string) => void;
}

function OfficeSelector({ officeId, setOfficeId }: OfficeSelectorProps) {
  const { api } = useApiV1Ctx();
  const { data: offices } = api.offices.listAllOffices.useQuery(undefined, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const options = useMemo(() => {
    return (
      offices?.map((office) => ({
        value: office.id,
        label: office.name,
      })) ?? []
    );
  }, [offices]);
  const selected = useMemo(() => {
    return options.find((option) => option.value === officeId);
  }, [options, officeId]);

  return (
    <Popover
      align='start'
      trigger={
        <Button variant='outline' className='max-w-52 justify-start truncate'>
          Office: {selected?.label}
        </Button>
      }
    >
      <SearchSelectContent
        options={options}
        selected={selected?.value ?? ''}
        onSelect={(value) => setOfficeId(value)}
      />
    </Popover>
  );
}

interface EmployeeTableProps {
  officeId: string;
}

function EmployeeTable({ officeId }: EmployeeTableProps) {
  console.log('officeId (table): ', officeId);
  const { api } = useApiV1Ctx();
  const { data, isLoading } = api.employees.adminListEmployees.useQuery(
    {
      query: {
        office_id: officeId,
      },
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const table = useReactTable({
    data: data?.items ?? [],
    columns: ADMIN_EMPLOYEE_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return <Skeleton className='h-96 w-full' />;
  }

  return (
    <div className='overflow-hidden rounded-md border'>
      <DataTable table={table} columns={ADMIN_EMPLOYEE_COLUMNS} />
    </div>
  );
}
